import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';
import { licenseService, License } from '../services/licenseService';
import { getApiCollection, getProp } from '../utils/apiResponse';
import { styles } from '../utils/styles';

// Map SKU part numbers to friendly names
const getFriendlyLicenseName = (skuPartNumber: string): string => {
  const licenseNames: { [key: string]: string } = {
    // Microsoft 365
    SPE_E3: 'Microsoft 365 E3',
    SPE_E5: 'Microsoft 365 E5',
    SPE_F1: 'Microsoft 365 F1',
    SPE_F3: 'Microsoft 365 F3',
    O365_BUSINESS_ESSENTIALS: 'Microsoft 365 Business Basic',
    O365_BUSINESS_PREMIUM: 'Microsoft 365 Business Standard',
    SPB: 'Microsoft 365 Business Premium',
    M365_F1_COMM: 'Microsoft 365 F1',

    // Office 365
    ENTERPRISEPACK: 'Office 365 E3',
    ENTERPRISEPREMIUM: 'Office 365 E5',
    DESKLESSPACK: 'Office 365 F3',
    STANDARDPACK: 'Office 365 E1',
    STANDARDWOFFPACK: 'Office 365 E2',

    // Exchange
    EXCHANGESTANDARD: 'Exchange Online Plan 1',
    EXCHANGEENTERPRISE: 'Exchange Online Plan 2',
    EXCHANGEDESKLESS: 'Exchange Online Kiosk',
    EXCHANGEESSENTIALS: 'Exchange Online Essentials',

    // SharePoint
    SHAREPOINTSTANDARD: 'SharePoint Online Plan 1',
    SHAREPOINTENTERPRISE: 'SharePoint Online Plan 2',

    // Teams
    TEAMS_EXPLORATORY: 'Microsoft Teams Exploratory',
    TEAMS1: 'Microsoft Teams Essentials',
    MCOSTANDARD: 'Microsoft Teams',

    // Visio & Project
    VISIOCLIENT: 'Visio Plan 2',
    VISIOONLINE_PLAN1: 'Visio Plan 1',
    PROJECTPREMIUM: 'Project Plan 5',
    PROJECTPROFESSIONAL: 'Project Plan 3',
    PROJECTESSENTIALS: 'Project Plan 1',

    // Power Platform
    POWER_BI_PRO: 'Power BI Pro',
    POWER_BI_STANDARD: 'Power BI Free',
    POWERAUTOMATE_ATTENDED_RPA: 'Power Automate per user with attended RPA',
    POWERFLOW_P2: 'Power Automate per user',
    POWERAPPS_PER_USER: 'Power Apps per user',

    // Security & Compliance
    EMSPREMIUM: 'Enterprise Mobility + Security E5',
    EMS: 'Enterprise Mobility + Security E3',
    AAD_PREMIUM: 'Azure Active Directory Premium P1',
    AAD_PREMIUM_P2: 'Azure Active Directory Premium P2',
    INTUNE_A: 'Microsoft Intune',
    THREAT_INTELLIGENCE: 'Microsoft Defender for Office 365 Plan 2',
    ATP_ENTERPRISE: 'Microsoft Defender for Office 365 Plan 1',

    // Other
    STREAM: 'Microsoft Stream',
    FLOW_FREE: 'Power Automate Free',
    POWERAPPS_VIRAL: 'Power Apps Trial',
    WIN10_VDA_E5: 'Windows 10/11 Enterprise E5',
    WIN10_VDA_E3: 'Windows 10/11 Enterprise E3',
  };

  return licenseNames[skuPartNumber] || skuPartNumber;
};

export const Licenses: React.FC = () => {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadLicenses();
  }, []);

  const loadLicenses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await licenseService.getLicenses();
      const licenses = getApiCollection<License>(response, 'loading licenses');
      setLicenses(licenses);
    } catch (err: any) {
      setError(err.message || 'Failed to load licenses');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Licenses
      </Typography>

      {error && (
        <Alert severity="error" sx={styles.errorAlert}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={styles.tableHead}>
            <TableRow>
              <TableCell sx={styles.tableHeaderCell}>License Name</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Total</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Consumed</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Available</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Usage</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {licenses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No licenses were returned. If you expected license data, verify API permissions
                  and tenant data.
                </TableCell>
              </TableRow>
            ) : (
              licenses.map((license: any) => {
                const prepaidUnits = getProp(license, 'prepaidUnits') || {};
                const enabled = getProp(prepaidUnits, 'enabled') || 0;
                const consumed = getProp(license, 'consumedUnits') || 0;
                const available = enabled - consumed;
                const usagePercent = enabled > 0 ? (consumed / enabled) * 100 : 0;
                const skuPartNumber = getProp(license, 'skuPartNumber');
                const friendlyName = getFriendlyLicenseName(skuPartNumber);

                return (
                  <TableRow key={getProp(license, 'skuId')}>
                    <TableCell>{friendlyName}</TableCell>
                    <TableCell>{enabled}</TableCell>
                    <TableCell>{consumed}</TableCell>
                    <TableCell>{available}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Box width="100%" mr={1}>
                          <LinearProgress
                            variant="determinate"
                            value={usagePercent}
                            color={
                              usagePercent > 90
                                ? 'error'
                                : usagePercent > 75
                                  ? 'warning'
                                  : 'success'
                            }
                          />
                        </Box>
                        <Box minWidth={35}>
                          <Typography variant="body2" color="text.secondary">
                            {usagePercent.toFixed(0)}%
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
